import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  useTheme,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth.store';
import { hasAnyAdminAccess } from '../utils/auth.helpers';
import AuthPageShell from '../components/auth/AuthPageShell';
import { authFieldSx, authSubmitButtonSx } from '../components/auth/authPage.styles';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const { login, error, pilotAccessDenied, isLoading, logout, clearError } = useAuthStore();
  const navigate = useNavigate();
  const theme = useTheme();

  const isPhone = (value: string): boolean =>
    value.startsWith('+966') || /^05\d/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const credentials = isPhone(username)
      ? { phone: username, password }
      : { username, password };
    setAccessError(null);
    const result = await login(credentials);
    if (result) {
      const state = useAuthStore.getState();
      if (!hasAnyAdminAccess(state.tokenPayload, state.isPlatformAdmin)) {
        await logout();
        setAccessError(t('auth.noAccess'));
        return;
      }
      navigate('/');
    }
  };

  return (
    <>
    <AuthPageShell title={t('auth.signIn')} subtitle={t('auth.signInSubtitle')}>
      {(error || accessError) && !pilotAccessDenied && (
        <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: 1 }}>
          {accessError || error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="username"
          label={t('auth.usernameOrPhone')}
          name="username"
          autoComplete="username"
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          sx={{ ...authFieldSx, mt: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('auth.password')}
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={authFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={
                    showPassword ? t('auth.hidePassword') : t('auth.showPassword')
                  }
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            underline="hover"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'primary.main',
            }}
          >
            {t('auth.forgotPassword')}
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={authSubmitButtonSx(theme)}
        >
          {isLoading ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </Box>
    </AuthPageShell>
    <Dialog
      open={pilotAccessDenied}
      onClose={clearError}
      aria-labelledby="pilot-mode-dialog-title"
      aria-describedby="pilot-mode-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        id="pilot-mode-dialog-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}
      >
        <LockRoundedIcon color="primary" />
        {t('auth.pilotModeTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="pilot-mode-dialog-description">
          {t('auth.pilotModeMessage')}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={clearError} variant="contained" autoFocus>
          {t('auth.backToSignIn')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Login;
